import { FC, useMemo, useState } from "react";

import { CreateReportAction } from "@vviinn/user-managment-client-ts";
import { CreatePlanAction } from "@vviinn/user-managment-client-ts/dist/models/CreatePlanAction";

import { PureVoidFunc } from "../../../../helpers/functions/types";
import { Fields } from "../../../../ui/layout/detailedPages/Fields";
import { NoDataMessage } from "../../../../ui/layout/detailedPages/NoDataMessage";
import { ShowAllButton } from "./ShowAllButton";
import { CompanyPricingOverlay } from "./CompanyPricingOverlay";
import { CompanyPricingItemTypes } from "./types";
import { AppearedContent } from "../../../../components/DataDisplay/AppearedContent";
import {
  DynamicFormCardWrapper,
  DynamicFormCardWrapperProps,
} from "./DynamicFormCardWrapper";
import { ToggleModalButtonProps } from "../../../../components/Interaction/Buttons/ToggleModalButton";
import { SubSectionWithStyles } from "../../../../ui/layout";

const createButtonProps: { [key: string]: ToggleModalButtonProps } = {
  plans: {
    createPermissions: "company:plans:create",
    buttonTitle: "Create New Plan",
    iconType: "new",
  },
  reports: {
    createPermissions: "company:reports:update",
    buttonTitle: "Generate reports",
    iconType: "refresh",
  },
};

const DEFAULT_SHOW_LIMIT = 3;
const LIST_ITEMS_STYLE =
  "grid grid-cols-1 divide-y divide-stone-100 dark:divide-black";

export const CompanyPricingItem: FC<{
  initShowAll?: boolean;
  itemCreateAction?: CreatePlanAction | CreateReportAction;
  type: CompanyPricingItemTypes;
  warning?: string;
  showAllButton?: {
    handleShow?: PureVoidFunc;
    disabled?: boolean;
    disabledMessage?: string;
  };
  cardsData: DynamicFormCardWrapperProps[];
  filterData?: (
    cardsData: DynamicFormCardWrapperProps[],
  ) => DynamicFormCardWrapperProps[];
}> = ({
  initShowAll = false,
  itemCreateAction,
  type,
  warning,
  showAllButton,
  cardsData,
  filterData,
}) => {
  const [openSlideOver, setOpenSlideOver] = useState(false);
  const [showAll, setShowAll] = useState(initShowAll);

  const relevantCards = useMemo(
    () =>
      showAll
        ? cardsData
        : filterData
          ? filterData(cardsData)
          : cardsData.slice(0, DEFAULT_SHOW_LIMIT),
    [cardsData, filterData, showAll],
  );

  const sectionCustomProps = useMemo(() => {
    return type === "invoices"
      ? {}
      : {
          isSubHeader: false,
          buttonProperties: {
            ...createButtonProps[type],
            onClick: () => setOpenSlideOver(true),
          },
        };
  }, [type]);

  return (
    <SubSectionWithStyles
      title={type !== "reports" ? type : "Billing"}
      withGroup={false}
      {...sectionCustomProps}
    >
      <Fields title={type === "reports" ? "reports" : undefined}>
        {cardsData.length ? (
          <div className={LIST_ITEMS_STYLE}>
            {relevantCards.map((data, index) => (
              <DynamicFormCardWrapper key={`${type}${index}`} {...data} />
            ))}
          </div>
        ) : (
          <NoDataMessage>
            {warning ?? (
              <AppearedContent
                isLoaded={false}
                skeletonOptions={{
                  amount: 1,
                  skeletonStyle: "rowSmStyle",
                }}
              />
            )}
          </NoDataMessage>
        )}
        {(showAllButton?.handleShow ||
          cardsData.length > DEFAULT_SHOW_LIMIT) && (
          <ShowAllButton
            {...showAllButton}
            title={type}
            showAll={showAll}
            handleShow={() => {
              showAllButton?.handleShow?.();
              setShowAll((prevState) => !prevState);
            }}
          />
        )}
      </Fields>
      {itemCreateAction && type !== "invoices" && (
        <CompanyPricingOverlay
          type={type}
          open={openSlideOver}
          setOpen={setOpenSlideOver}
          itemCreateAction={itemCreateAction}
        />
      )}
    </SubSectionWithStyles>
  );
};
